'use client'

import { motion } from "framer-motion"
import { BookOpenText, ChevronRight } from "lucide-react"
import { formatDateNormal, formatImageURL, resolveColorHex, getVendorPrimaryColor } from "@/utils/util"
import { getReadingTime, getExcerpt } from "@/utils/blog"
import { navigateUrl } from "@/utils/client-request"
import { usePathname } from "next/navigation"
import EmptyState from "@/components/elements/EmptyState"

export default function BlogList({ blogs, vendor }: { blogs: any[], vendor?: any }) {
  const pathname = usePathname()
  const primary = resolveColorHex(getVendorPrimaryColor(vendor))
  const vendorName = vendor?.name || 'Дэлгүүр'
  const sorted = [...(blogs || [])].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

  if (!sorted.length) {
    return (
      <div className="cont py-10 sm:py-8">
        <BlogNotFound />
      </div>
    )
  }

  const [hero, ...rest] = sorted
  const sub = rest.slice(0, 2)
  const grid = rest.slice(2)

  return (
    <div className="bg-white">
      <motion.div initial={{ opacity: 0, y: -10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0, duration: 0.5, ease: "easeIn" }} viewport={{ once: true }} className="mx-auto w-full max-w-320 px-4 lg:px-8 py-8">
        <div className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground mb-8">
          <a href={navigateUrl('/', pathname)} className="hover:text-foreground">Нүүр</a>
          <ChevronRight className="h-3 w-3 opacity-50" />
          <span className="text-foreground font-medium">Блог</span>
        </div>

        <div className="max-w-2xl mb-10">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
            Блог
          </h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl leading-relaxed">
            {vendorName}-ийн шинэ мэдээ, зөвлөмжүүдийг эндээс уншина уу.
          </p>
        </div>

        <a href={navigateUrl(`/blog/${hero.id}`, pathname)} className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 cursor-pointer group">
          <div className="lg:col-span-7">
            <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-muted">
              <img src={formatImageURL(hero.thumbnail || 'placeholder.png')} alt={hero.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
            </div>
          </div>
          <div className="lg:col-span-5 flex flex-col justify-end">
            <div className="inline-flex items-center w-fit gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: `color-mix(in oklch, ${primary} 12%, white)`, color: primary }}>Онцлох</div>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight leading-snug">{hero.title}</h2>
            {getExcerpt(hero.content) && (
              <p className="mt-4 text-[15px] sm:text-base leading-[1.6] text-muted-foreground max-w-xl line-clamp-3">{getExcerpt(hero.content)}</p>
            )}
            <div className="mt-5 flex items-center gap-3 text-xs text-muted-foreground tabular-nums">
              <span>{formatDateNormal(new Date(hero.createdAt))}</span>
              <span className="w-1 h-1 rounded-full bg-muted-foreground/40" />
              <span>{getReadingTime(hero.content)} мин уншина</span>
            </div>
          </div>
        </a>

        {sub.length > 0 && (
          <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
            {sub.map((p) => (
              <a key={p.id} href={navigateUrl(`/blog/${p.id}`, pathname)} className="cursor-pointer group">
                <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-muted">
                  <img src={formatImageURL(p.thumbnail || 'placeholder.png')} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
                </div>
                <div className="mt-4 text-xs text-muted-foreground tabular-nums">{formatDateNormal(new Date(p.createdAt))}</div>
                <h3 className="mt-2 text-xl font-semibold tracking-tight leading-snug">{p.title}</h3>
                {getExcerpt(p.content) && (
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2 leading-relaxed">{getExcerpt(p.content, 140)}</p>
                )}
                <div className="mt-3 text-xs text-muted-foreground tabular-nums">{getReadingTime(p.content)} мин уншина</div>
              </a>
            ))}
          </div>
        )}

        {grid.length > 0 && (
          <div className="mt-12 border-t border-border pt-10 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-12">
            {grid.map((p) => (
              <a key={p.id} href={navigateUrl(`/blog/${p.id}`, pathname)} className="cursor-pointer group">
                <div className="aspect-[4/3] rounded-xl overflow-hidden bg-muted mb-4">
                  <img src={formatImageURL(p.thumbnail || 'placeholder.png')} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                </div>
                <div className="text-xs text-muted-foreground tabular-nums">{formatDateNormal(new Date(p.createdAt))} · {getReadingTime(p.content)} мин</div>
                <h4 className="mt-2 text-[20px] font-semibold tracking-[-0.005em] leading-[1.25]">{p.title}</h4>
                {getExcerpt(p.content, 120) && (
                  <p className="mt-1.5 text-[13px] text-muted-foreground line-clamp-2 leading-relaxed">{getExcerpt(p.content, 120)}</p>
                )}
              </a>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  )
}

export function BlogNotFound() {
  return <EmptyState icon={BookOpenText} title="Нийтлэл олдсонгүй" description="Яг одоогоор ямар нэгэн нийтлэл байхгүй байна. Хэсэг хугацааны дараа дахин шалгана уу." />
}
